/// FOURJS_START_COPYRIGHT(D,2018)
/// Property of Four Js*
/// (c) Copyright Four Js 2018, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

/*
 see tools/doc/internaldoc/UR/implement.md for documentation
 bootstrapper for gbc environment
 will let embedding platforms do their job
 */

(function (context) {
  let runReady = function (readyData) {
    if (context.gbcWrapper._readyData.isLogReplay) {
      let procId =
        context.gbcWrapper.protocolVersion && context.gbcWrapper.protocolVersion >= 2
          ? context.gbc.classes.AuiProtocolReader.translate(readyData.meta)[0].attributes.procId
          : "0";
      const app = context.gbc.LogPlayerService.getApplication(0, null, procId);
      let receiveHook = context.gbcWrapper.on(context.gbcWrapper.events.RECEIVE, function (event, src, data) {
        let whenManaged = function () {
          if (app.ending || app.ended) {
            context.gbcWrapper.logProcessed(context.gbcWrapper.param(null, app));
          } else {
            app.layout.afterLayout(function () {
              context.gbcWrapper.logProcessed(context.gbcWrapper.param(null, app));
            }, true);
          }
        };
        if (app && !app.ending && !app.ended) {
          if (context.gbcWrapper.protocolVersion && context.gbcWrapper.protocolVersion >= 2) {
            if (data.procId === app.info().procId) {
              gbc.LogService.networkProtocol.log(`⇓ LOG-RCV order`, data.content);
              app.dvm.manageAuiOrders(data.content, whenManaged);
            }
          } else {
            app.dvm.manageAuiOrders(data, whenManaged);
          }
        }
      });
      app
        .getSession()
        .getNavigationManager()
        .onApplicationRemoved((event, src, application) => {
          if (application === app && receiveHook) {
            receiveHook();
          }
        }, true);
      context.gbcWrapper.URReady(
        context.gbcWrapper.param(
          {
            UCName: "GBC",
            UCVersion: gbc.version,
            mobileUI: gbc.ThemeService.getValue("gbc-aui-mobileUI-default") ? 1 : 0,
            media: gbc.ThemeService.getMediaString(),
            theme: gbc.ThemeService.getCurrentTheme()
          },
          {
            procId: procId
          }
        )
      );
    } else {
      context.gbc.SessionService.startDirect(context.gbcWrapper, readyData);
    }
  };

  let readyCallbacks = [],
    /**
     *
     * @param event
     * @param src
     * @param data
     */
    doReadyCallbacks = function (event, src, data) {
      if (!context.gbcWrapper.ready) {
        // TODO why we update data only for the first application started ?
        context.gbcWrapper._readyData = { ...context.gbcWrapper._readyData, ...data };
        console.info("GBC Wrapper readyData: ", context.gbcWrapper._readyData);
        context.gbcWrapper.ready = true;
        let i = 0,
          len = readyCallbacks.length;
        for (; i < len; i++) {
          readyCallbacks[i]();
        }
        readyCallbacks = [];
      } else {
        console.info("GBC Wrapper readyData (NOT SAVED): ", data);
        runReady(data);
      }
    };
  /**
   * @namespace window.gbcWrapper
   */
  context.gbcWrapper = {
    events: {
      /**
       * Event ready
       * Fired once when platform declares its low level bindings are ready
       */
      READY: "ready",
      /**
       * Event close
       * Fired when platform's close button is clicked
       */
      CLOSE: "close",
      /**
       * Event end
       * Fired when an application ends
       */
      END: "end",
      /**
       * Event receive
       * Fired when data are received
       */
      RECEIVE: "receive",
      /**
       * Event nativeAction
       * Fired when native sends an action to be managed by UR (e.g. pushNotification, cordovaCallback
       */
      NATIVEACTION: "nativeAction",
      /**
       * Event destroyEvent
       * Fired when native sends a destroyEvent to be managed by UR
       */
      DESTROYEVENT: "destroyEvent",
      /**
       * Event debugNode
       * Fired when debugNode is selected in the debugger
       */
      DEBUGNODE: "debugNode",
      /**
       * Event servicesReady
       * Fired when all the services are initialized
       */
      SERVICESREADY: "servicesready",
      /**
       * Event gbcCall
       * Fired when UR wants to send info to GBC
       */
      GBCCALL: "gbcCall"
    },
    /**
     * true when all platform bindings are ready to use
     */
    ready: false,

    /**
     * @typedef {Object} FontData - data related to font to set
     * @property {String} [size] - font size as defined in CSS spec (with units like px, em, pt...)
     * @property {String} [family] - font family name
     * @property {number} [weight] - font weight between 100-900 (default is 400)
     * @property {Boolean} [italic] - font is italic if true, false otherwise
     */

    /**
     * @typedef {Object} ReadyData
     * @property {Object<string, *>} [headers] - headers are only needed when it is a GAS connection
     * @property {string} [meta] - the meta connection string "meta connection …"
     * @property {boolean} [debugMode] - true to activate UR debug mode
     * @property {boolean} [isLogReplay] - true if it is a log replay
     * @property {Number} [logLevel] - the log level
     * @property {string} [language] - language "FR_fr"
     * @property {FontData} [font] - Font data
     * @property {string} nativeResourcePrefix - is mandatory, it should fit the host that delivered the UR ("file://__ur__.4js.com/"), beware, in direct mode this value is unique for each application
     * @property {boolean} [useBrowserFrontCalls] - Set use of browser or native frontcalls by default
     * @property {Object<string, string|Array<string>>} [forcedURfrontcalls] - Map of functioncall name per module to be directly executed by UR and not asking native.
     */

    /**
     * application start data
     * @type {ReadyData}
     */
    _readyData: {
      headers: {},
      meta: "",
      debugMode: false,
      isLogReplay: false,
      logLevel: 1,
      language: "",
      font: {},
      nativeResourcePrefix: "",
      useBrowserFrontCalls: context.gbcWrapperInfo.platformType === "browser",
      forcedURfrontcalls: {
        theme: "*",
        wci: "*",
        webcomponent: "*",
        gbcqa: "*"
      }
    },

    /**
     * platform type
     * can be "browser" or "native"
     */
    platformType: context.gbcWrapperInfo.platformType || "browser",
    /**
     * platform name
     * can be "browser", "GDC", "GMA", "GMI", "GWA"
     */
    platformName: context.gbcWrapperInfo.platformName || "browser",
    /**
     * protocol type
     * can be "ua" or "direct"
     */
    protocolType: context.gbcWrapperInfo.protocolType || "ua",
    /**
     * protocol version
     */
    protocolVersion: context.gbcWrapperInfo.protocolVersion,

    /**
     * inform the platform that Universal Renderer is fully initialized and wait for data
     * param {Object<string, *>} options additional information for the protocol
     */
    URReady: function (attr) {},

    /**
     * send data through the platform
     * @param {string} data stringified aui order
     * @param {Object<string, *>} options additional information for the protocol
     *   e.g. in no user activity actions, options will be set to {userActivity: "no"}
     */
    send: function (data, options) {},

    /**
     * inform the platform about a childstart
     */
    childStart: function () {},

    /**
     * send interrupt signal through the platform
     */
    interrupt: function () {},

    /**
     * send close signal through the platform
     */
    close: function () {},

    /**.
     * @callback frontcallCallback
     * @param {{status:number, result:string, errorMessage:string}} result
     */

    /**
     * call the platform's frontcall
     * @param {number} nodeId
     * @param {frontcallCallback} callback
     */
    frontcall: function (nodeId, callback) {},

    /**
     * Show the native aui debugger
     * @param {Number} data the node id or -1
     */
    showDebugger: function (data) {
      const url = context.gbc.UrlService.currentUrl();
      window.open(url.removeQueryString("app").addQueryString("monitor", true).toString());
    },
    /**
     * Returns true if close icon button should be displayed in the ChromeBar
     * @return {*|boolean}
     */
    isCloseButtonVisibleInChromeBar: function () {
      // Never display close button in GDC or GMA (managed by native client)
      if (this.isPlatformTypeGDC() || this.isPlatformTypeGMA()) {
        return false;
      }
      return context.gbc.MainContainerService.isChromeBarVisible();
    },
    /**
     * Returns true if the close action is allowed to be displayed in the default view (actionPanel, ringMenuPanel).
     * The chromebar visibility is NOT checked here: when defaultView=yes, close can be displayed in both
     * the chromebar AND the action panel. The caller is responsible for the additional `actionPanelPosition !== "chrome"`
     * check (typically via `controller.isInChromeBar()`).
     * @param {string} defaultView value of the close action
     * @return {boolean}
     */
    isCloseButtonVisibleInDefaultView: function (defaultView) {
      // Never display close button in GDC or GMA (managed by native client)
      if (this.isPlatformTypeGDC() || this.isPlatformTypeGMA()) {
        return false;
      }
      return defaultView === context.gbc.constants.viewType.showAlways;
    },
    /**
     * Return true if the offline mode handling is enabled
     * @return {boolean}
     */
    isOfflineModeEnabled: function () {
      if (this.isPlatformTypeGDC() || this.isPlatformTypeGMA() || this.isPlatformTypeGMI() || this.isPlatformTypeGWA()) {
        return false;
      }
      return context.gbc.OfflineModeService.isServiceEnabled();
    },
    /**
     * Call a named native function
     * @param {{name:string, args:Array}} data native call info
     *
     * Possible call data are:
     *
     * > `{name:"windowTitle",args: [<document.title>]}`
     * > will update the document title to the native
     *
     * > `{name:"error",args: [<error message>]}`
     * > will send error messages to the native
     *
     * > `{name:"noBackAction"}`
     * > will inform the native there is no back resolved action (after a 'nativeAction' 'back')
     *
     * @return {Boolean} true if nativeCall executed correctly, false otherwise (false on web wrapper as not native)
     */
    nativeCall: function (data) {
      return false;
    },

    /**
     * Inform the native platform that a log was processed
     * Fired in UR log player mode when a log order is fully processed
     */
    logProcessed: function () {},

    /**
     * Transforms a resource path
     * @param {string} path raw path
     * @param {?string} prefix prefix if any
     * @param {string} nativeAppPrefix native app prefix
     * @return {string} the transformed resource path
     */
    wrapResourcePath: function (path, prefix, nativeAppPrefix) {
      path = "" + path; // force path to string if not
      // if path has a scheme, don't change it
      if (!path || /^(http[s]?:|[s]?ftp:|data:|file:|font:|genero-ft:)/i.test(path)) {
        return path;
      }

      let startPath = "";
      if (this.isPlatformTypeNative()) {
        startPath = nativeAppPrefix;
      }

      startPath += prefix ? prefix + "/" : "";

      // In case there is a queryString, we should not encode it, keep it aside before adding it to returnPath
      const queryIndex = path && path.indexOf("?");
      let queryStr = "";
      if (queryIndex > 0) {
        queryStr = path.substring(queryIndex);
        path = path.replace(queryStr, "");
      }

      // Normalize Windows paths (C:\path\to\file.ttf -> C:/path/to/file.ttf)
      if (this.isPlatformTypeNative() && /^[a-zA-Z]:\\/.test(path)) {
        path = path.replace(/\\/g, "/");
      }

      return startPath + path + queryStr;
    },

    /**
     * Returns session end URL, null for default end page
     * @return {?String} session end URL
     */
    getSessionEndUrl: function () {
      return null;
    },

    /**
     * fires event in gbcWrapper
     * @param {string} eventType the event type
     * @param {*} [data] any data to fire with
     * DO NOT IMPLEMENT
     */
    emit: function (eventType, data) {},

    /**
     * gbcWrapper initializer
     * GBC internal
     * DO NOT IMPLEMENT
     * @private
     */
    __init: function () {
      if (gbc.buildTool === "legacy") {
        console.info(
          "GBC",
          context.gbc.version + "-" + context.gbc.build,
          "- PlatformType:",
          this.platformType,
          "- PlatformName:",
          this.platformName,
          "- ProtocolType:",
          this.protocolType,
          "- ProtocolVersion:",
          this.protocolVersion
        );
      }

      context.gbcWrapper.on = function (eventType, eventHandler) {
        if (eventType === context.gbcWrapper.events.READY) {
          if (eventHandler instanceof Function) {
            if (this.ready) {
              eventHandler();
            } else {
              if (this._eventListener) {
                this._eventListener.when(eventType, eventHandler, true);
              } else {
                readyCallbacks.push(eventHandler);
              }
            }
            return;
          } else {
            throw new Error("gbcWrapper: onReady callback is not a function");
          }
        }
        if (this._eventListener) {
          return this._eventListener.when(eventType, eventHandler);
        }
      };
      if (context.gbcWrapper.isProtocolTypeDirect()) {
        context.__gbcDefer = function (start) {
          start();
        };
      }
    },

    /**
     * gbcWrapper prepare wrapper before real start
     * GBC internal
     * DO NOT IMPLEMENT
     * @private
     */
    __prepare: function () {
      const listener = (context.gbcWrapper._eventListener = new context.gbc.classes.EventListener());
      context.gbcWrapper.emit = function (eventType, data) {
        this._eventListener.emit(eventType, data);
      };
      listener.when(context.gbcWrapper.events.READY, doReadyCallbacks);

      // if protocoleType is UA, READY event is sent immediately to start GBC
      // else it is the client which must send READY event
      if (context.gbcWrapper.isProtocolTypeUA()) {
        this.emit(context.gbcWrapper.events.READY);
      }
    },
    /**
     * gbcWrapper ending wrapper initialization after GBC ready
     * GBC internal
     * DO NOT IMPLEMENT
     * @private
     */
    __gbcReady: function () {
      if (context.gbcWrapper.isProtocolTypeDirect()) {
        context.gbcWrapper.on(context.gbcWrapper.events.NATIVEACTION, function (event, src, data) {
          context.gbc.NativeService.onNativeAction(data);
        });
        context.gbcWrapper.on(context.gbcWrapper.events.DESTROYEVENT, function (event, src, data) {
          context.gbc.NativeService.onDestroyEvent(data);
        });
        context.gbcWrapper.on(context.gbcWrapper.events.CLOSE, function () {
          context.gbc.NativeService.onNativeAction({
            name: "close"
          });
        });
        context.gbcWrapper.on(context.gbcWrapper.events.END, function (event, src, data) {
          context.gbc.NativeService.onNativeEnd(data);
        });

        // Generic Call from UR to GBC
        context.gbcWrapper.on(context.gbcWrapper.events.GBCCALL, function (event, src, data) {
          context.gbc.NativeService.onGbcCall(data);
        });

        context.gbcWrapper.on(context.gbcWrapper.events.READY, function () {
          // *** _readyData.headers ***
          // NOTHING

          // *** _readyData.meta ***
          // NOTHING

          // *** _readyData.debugMode ***
          if (context.gbcWrapper._readyData.debugMode === true) {
            context.gbc.DebugService.activate();
          }

          // *** _readyData.isLogReplay ***
          // NOTHING

          // *** _readyData.logLevel ***
          /**
           ~~ Notes about log level ~~
           "none"  is LogLevel 0 : only sent when GDC is launched without debug mode
           "error" is LogLevel 1 : param LOWEST  in GDC
           "warn"  is LogLevel 2 : param LOW     in GDC
           "info", "log", is LogLevel 3 : param HIGH in GDC
           "debug" is LogLevel 4 : param HIGHEST in GDC
           */
          const logLevel = context.gbcWrapper._readyData.logLevel;
          if (logLevel < 3) {
            context.gbc.LogService.changeLevel("info");
          } else {
            context.gbc.LogService.changeLevel("debug");
          }

          // Always activate all log types because settings can be loaded from stored settings
          // TODO we should not do this, settings must not be loaded from storedsettings in this case
          const types = [
            "_default",
            "keyboard",
            "clipboard",
            "mouse",
            "ui",
            "scheduler",
            "focus",
            "gICAPI",
            "networkProtocol",
            "sw",
            "input",
            "ClientQA"
          ];
          for (const elt of types) {
            context.gbc.LogService.activateType(elt);
          }

          // *** _readyData.language ***
          if (context.gbcWrapper._readyData.language) {
            context.gbc.I18NService.setLng(context.gbcWrapper._readyData.language);
          }

          // *** _readyData.fontData ***
          if (context.gbcWrapper._readyData.font) {
            context.gbc.ThemeService.setFontData(context.gbcWrapper._readyData.font);
          }

          // *** _readyData.nativeResourcePrefix ***
          if (!context.gbcWrapper._readyData.nativeResourcePrefix) {
            throw new Error("gbcWrapper: onReady data did not contain a valid 'nativeResourcePrefix'");
          }

          // *** _readyData.useBrowserFrontCalls ***
          context.gbc.FrontCallService.setUseBrowserFrontCalls(Boolean(context.gbcWrapper._readyData.useBrowserFrontCalls));

          // *** _readyData.forcedURfrontcalls ***
          context.gbc.FrontCallService.setForcedURFrontCalls(context.gbcWrapper._readyData.forcedURfrontcalls);

          runReady(context.gbcWrapper._readyData);
        });
      }
    },

    /**
     * returns if gbc runs whether in native mode or not
     * GBC internal
     * DO NOT IMPLEMENT
     * @return {boolean} true if gbc runs in native mode
     */
    isPlatformTypeNative: function () {
      return this.platformType === "native";
    },

    /**
     * returns if gbc runs whether in browser mode or not
     * GBC internal
     * DO NOT IMPLEMENT
     * @return {boolean} true if gbc runs in browser mode
     */
    isPlatformTypeBrowser: function () {
      return this.platformType === "browser";
    },
    /**
     * returns true if gbc runs in GMI
     * GBC internal
     * DO NOT IMPLEMENT
     * @return {boolean} true if gbc runs in GMI
     */
    isPlatformTypeGMI: function () {
      return this.platformName === "GMI";
    },
    /**
     * returns true if gbc runs in GMA
     * GBC internal
     * DO NOT IMPLEMENT
     * @return {boolean} true if gbc runs in GMA
     */
    isPlatformTypeGMA: function () {
      return this.platformName === "GMA";
    },
    /**
     * returns true if gbc runs in GDC
     * GBC internal
     * DO NOT IMPLEMENT
     * @return {boolean} true if gbc runs in GDC
     */
    isPlatformTypeGDC: function () {
      return this.platformName === "GDC";
    },
    /**
     * returns true if gbc runs in GWA
     * GBC internal
     * DO NOT IMPLEMENT
     * @return {boolean} true if gbc runs in GWA
     */
    isPlatformTypeGWA: function () {
      return this.platformName === "GWA";
    },
    /**
     * returns if gbc use "direct" protocol
     * GBC internal
     * DO NOT IMPLEMENT
     * @return {boolean} true if gbc use "direct" protocol
     */
    isProtocolTypeDirect: function () {
      return this.protocolType === "direct";
    },

    /**
     * returns if gbc use "ua" protocol
     * GBC internal
     * DO NOT IMPLEMENT
     * @return {boolean} true if gbc use "ua" protocol
     */
    isProtocolTypeUA: function () {
      return this.protocolType === "ua";
    },

    param: function (data, application) {
      if (this.protocolVersion && this.protocolVersion >= 2) {
        if (data || typeof data === "string" || typeof data === "boolean" || typeof data === "number") {
          return {
            procId: (application && application.procId) || "",
            content: data
          };
        } else {
          return {
            procId: (application && application.procId) || ""
          };
        }
      } else {
        return data;
      }
    }
  };
})(window);
